import React from "react";
import PropTypes from "prop-types";
import { formatBreakupCellValue } from "./salaryBreakupUtils";

/**
 * Pure presentational table component for Old Scheme Salary Breakup Matrix.
 * 
 * Follows Single Responsibility Principle (SRP):
 * Only handles rendering the structured comparison matrix without concern
 * for modal state or API retrieval.
 */
const SalaryBreakupTable = ({ columns, sections }) => {
  if (!columns || columns.length === 0) {
    return (
      <div className="py-12 text-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <p className="text-gray-500 font-medium text-sm">
          No salary breakup records available for this payout period.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      <div className="overflow-x-auto max-h-[60vh]">
        <table className="w-full divide-y divide-gray-200 text-left table-auto border-collapse">
          {/* Main Table Header */}
          <thead className="sticky top-0 z-20">
            <tr className="bg-[color:var(--color1)] text-white divide-x divide-white/20">
              <th
                scope="col"
                className="sticky left-0 z-30 px-4 py-3 text-xs font-bold uppercase tracking-wider text-left bg-[color:var(--color1)] border-r border-white/20 min-w-[220px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.2)]"
              >
                Salary Component
              </th>
              {columns.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-center bg-[color:var(--color1)] min-w-[170px]"
                >
                  <div className="flex flex-col items-center justify-center gap-1">
                    <span>{col.title}</span>
                    {col.dateRange ? (
                      <span className="text-[11px] font-semibold text-white/95 bg-white/20 px-2.5 py-0.5 rounded-full whitespace-nowrap tracking-normal shadow-sm normal-case">
                        {col.dateRange}
                      </span>
                    ) : (
                      col.subTitle && (
                        <span className="text-[10px] font-normal text-white/80 normal-case">
                          {col.subTitle}
                        </span>
                      )
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {sections.map((section) => (
              <React.Fragment key={section.id}>
                {/* Section Header Row (Full Width spanning) */}
                <tr className="bg-gray-100/90 font-bold border-t-2 border-b border-gray-300">
                  <td
                    colSpan={columns.length + 1}
                    className="sticky left-0 px-4 py-2 text-xs uppercase tracking-wider font-extrabold text-gray-800 bg-gray-100"
                  >
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-full ${
                          section.id === "earnings"
                            ? "bg-blue-600"
                            : section.id === "deduction"
                            ? "bg-amber-600"
                            : section.id === "contributions"
                            ? "bg-purple-600"
                            : "bg-emerald-600"
                        }`}
                      />
                      <span>{section.title}</span>
                    </div>
                  </td>
                </tr>

                {/* Section Child Data Rows */}
                {section.rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-blue-50/40 transition-colors divide-x divide-gray-200"
                  >
                    {/* Fixed Component Label (Left) */}
                    <td className="sticky left-0 z-10 px-4 py-2.5 text-xs font-medium text-gray-800 bg-gray-50/90 border-r border-gray-200 whitespace-nowrap shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)]">
                      {row.label}
                    </td>

                    {/* Data Cells per Column */}
                    {columns.map((col) => {
                      const rawVal = row.accessor(col.data);
                      const formattedVal = formatBreakupCellValue(
                        rawVal,
                        row.formatType
                      );
                      const isBoldRow =
                        row.id === "basic_da" ||
                        row.id === "net_salary" ||
                        row.id === "net_payable";

                      return (
                        <td
                          key={`${col.id}-${row.id}`}
                          className={`px-4 py-2.5 text-xs text-center whitespace-nowrap ${
                            isBoldRow
                              ? "font-bold text-gray-900 bg-gray-50/30"
                              : "text-gray-700"
                          }`}
                        >
                          {formattedVal}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

SalaryBreakupTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      subTitle: PropTypes.string,
      dateRange: PropTypes.string,
      data: PropTypes.object.isRequired,
    })
  ).isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          formatType: PropTypes.string.isRequired,
          accessor: PropTypes.func.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
};

export default React.memo(SalaryBreakupTable);
